/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.WizardService
 * @module mx.SolutionBuilderAgent
 *
 * @requires {@ref mx.SolutionBuilderAgent.DataModelService mx.SolutionBuilderAgent.DataModelService}
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.WizardView" target="_blank">mx.shell.WizardView</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.Wizard" target="_blank">mx.shell.Wizard</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.WizardPage" target="_blank">mx.shell.WizardPage</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.WizardButton" target="_blank">mx.shell.WizardButton</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.WizardRoadmap" target="_blank">mx.shell.WizardRoadmap</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.WizardRoadmapStep" target="_blank">mx.shell.WizardRoadmapStep</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.EventService" target="_blank">mx.shell.EventService</a>
 * @requires {@ref mx.SolutionBuilderAgent.ContentWidgetService mx.SolutionBuilderAgent.ContentWidgetService}
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$q" target="_blank">$q</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.NotificationService" target="_blank">mx.shell.NotificationService</a>
 * @requires <a href="https://github.com/Matrix42AG/PandoraComponents/wiki/mx.internationalization" target="_blank">mx.internationalization</a>
 * @requires {@ref mx.SolutionBuilderAgent.SchemaService mx.SolutionBuilderAgent.SchemaService}
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.SidePanelService" target="_blank">mx.shell.SidePanelService</a>
 * @requires {@ref mx.SolutionBuilderAgent.ExternalServiceManager mx.SolutionBuilderAgent.ExternalServiceManager}
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.SideLayout" target="_blank">mx.shell.SideLayout</a>
 * @requires {@ref mx.SolutionBuilderAgent.CacheService mx.SolutionBuilderAgent.CacheService}
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$injector" target="_blank">$injector</a>
 *
 * @description
 * A service provides methods for wizard view creation.
 */
WizardService.$inject = [
	'mx.SolutionBuilderAgent.DataModelService',
	'mx.shell.WizardView',
	'mx.shell.Wizard',
	'mx.shell.WizardPage',
	'mx.shell.WizardButton',
	'mx.shell.WizardRoadmap',
	'mx.shell.WizardRoadmapStep',
	'mx.shell.EventService',
	'mx.SolutionBuilderAgent.ContentWidgetService',
	'mx.SolutionBuilderAgent.ConcurrencyTrackingService',
	'$q',
	'mx.shell.NotificationService',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.SchemaService',
	'mx.shell.SidePanelService',
	'mx.SolutionBuilderAgent.ExternalServiceManager',
	'mx.shell.SideLayout',
	'mx.SolutionBuilderAgent.CacheService',
	'mx.SolutionBuilderAgent.ActionCoRuService',
	'mx.shell.ObjectMapper',
];
export default function WizardService(
	dataModelService,
	WizardView,
	Wizard,
	WizardPage,
	WizardButton,
	WizardRoadmap,
	WizardRoadmapStep,
	eventService,
	contentWidgetService,
	concurrencyTrackingService,
	$q,
	notificationService,
	i18n,
	schemaService,
	sidePanelService,
	externalServiceManager,
	sideLayoutService,
	cacheService,
	actionCoRuService,
	objectMapper
) {
	const pagesCommonController = 'mx.SolutionBuilderAgent.WizardPageController';

	let _gettingWizardAndDataModelPendingPromises = [];
	const wizardUiActionGroup = [];

	eventService.subscribeGlobal(mx.shell.EventCodes.WizardClose, function (e, data) {
		_freeWizardDataSources(data.wizard);
	});
	eventService.subscribeGlobal(mx.shell.EventCodes.SidePanelViewClosed, function (e, view) {
		_freeWizardDataSources(view && view.locals && view.locals.wizard);
	});

	return {
		getWizardView: getWizardView,
		getWizardAndDataModel: getWizardAndDataModel,
		processFinishEvents: processFinishEvents,
		showObjectChangedNotification: showObjectChangedNotification,
		showObjectDeletedNotification: showObjectDeletedNotification,
		wizardUiActionGroup: wizardUiActionGroup,
	};

	function _freeWizardDataSources(wizard) {
		wizard?.__freeDataSourceDefer?.resolve(true);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.WizardService#getWizardView
	 *
	 * @description
	 * Creates and returns WizardView
	 *
	 * @param {object} options - Wizard view options that may contain
	 *  {string} typeName - Type of object wizard view to be created for.
	 *  {string} widgetId - Related Content Widget Wizard identifier.
	 *  {string} actionId - An id of an action that triggered the wizard.
	 *  {string} actionName - A name of an action that triggered the wizard.
	 *  {number} wizardMode - Shows if wizard view is created for create or edit dialog.
	 *  {string} name - A title for view tab.
	 *  {string} displayName - A display name for view.
	 *  {string} finishTitle - A title for wizard's finish button.
	 *  {string} customHeader - A custom header text to view instead of default wizard's header.
	 *  {object[]} selectedObjects - An array of objects that describes current context.
	 *  {object} presetFields - An object with a Data Model preset values.
	 *  {object} outerContext - Context of entity that calls current wizard
	 *
	 * @returns {object} An object with initialized wizard in `wizard` field and initialized DataModel in `dataModel` field.
	 */
	function getWizardView(options) {
		const initViewWrapper = function (selectedObjectsFromView) {
			const freeDataSourceDefer = $q.defer();
			selectedObjectsFromView = selectedObjectsFromView || options.selectedObjects || [];
			//originalOptions is used in the merge process
			wizardView.locals.originalOptions = options;
			wizardView.locals.originalOptions.selectedObjects = selectedObjectsFromView;

			return getWizardAndDataModel(
				options.typeName,
				options.wizardMode,
				options.widgetId,
				wizardView,
				options.actionId,
				options.actionName,
				options.finishTitle,
				options.customHeader,
				selectedObjectsFromView,
				options.outerContext,
				undefined,
				freeDataSourceDefer.promise
			)
				.then(function (data) {
					if (data?.dataModel && options.presetFields) {
						$.extend(true, data.dataModel, options.presetFields);
					}
					if (data?.wizard) {
						data.wizard.__freeDataSourceDefer = freeDataSourceDefer;
					}

					if (options.wizardMode === mx.SolutionBuilderAgent.WizardMode.New || options.wizardMode === mx.SolutionBuilderAgent.WizardMode.Edit) {
						const entity = data.dataModel[data.dataModel.Context.__typeName];
						data.wizard.context = data.wizard.context || {};
						data.wizard.context.objectId = (entity && entity._id) || 'new';
						// originalOptions is used in the merge process
						if (options.wizardMode === mx.SolutionBuilderAgent.WizardMode.Edit) {
							data.wizard.context.originalOptions = wizardView.locals.originalOptions;
						}
					}
					return data;
				})
				.catch(function (e) {
					mx.console.error(e);
					return $q.reject(e);
				});
		};

		const wizardView = new WizardView({
			id: undefined,
			name: options.name,
			initView: initViewWrapper,
			displayName: options.displayName,
			controller: 'mx.SolutionBuilderAgent.WizardController',
			template:
				'<mx-wizard-view wizard="vm.wizard" data-dirty="vm.view.dirty" data-model="vm.dataModel" non-visual-template="vm.nonVisualTemplate" wizard-header-text="vm.wizardHeaderText" is-lazy="vm.isLazy" object-id="vm.objectId" merge-disabled="vm.view.mergeDisabled"></mx-wizard-view>',
			mergeDisabled: options.mergeDisabled,
			locals: {
				widgetId: options.widgetId,
			},
		});

		wizardView.uiActionGroup.push(...wizardUiActionGroup);
		wizardView.locals.view = wizardView;
		wizardView.headerTitle = i18n.get('solutionBuilderAgent.wizards.headerTitle');
		wizardView.actionId = options.actionId;

		return wizardView;
	}

	function prepareNonVisualControls(template) {
		let i = 0;
		let result = template.replace(new RegExp('<mx-extra-wizard ', 'g'), function () {
			return (
				'<mx-extra-wizard main-wizard="vm.dataModel.Context.__wizard" ' +
				'selected-objects="vm.dataModel.Context.__selectedObjects" ' +
				'position-in-main-wizard="' +
				i++ +
				'" type-name="{{:: vm.dataModel.Context.__typeName}}" '
			);
		});

		result = result.replace('<mx-wizard-manager ', '<mx-wizard-manager wizard="vm.dataModel.Context.__wizard"');
		result = result.replace('<mx-wizard-page-validate ', '<mx-wizard-page-validate wizard="vm.dataModel.Context.__wizard"');
		result = result.replace('<mx-validate-wizard ', '<mx-validate-wizard wizard="vm.dataModel.Context.__wizard"');

		return {
			template: result,
			numOfExtraWizards: i + 1,
		};
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.WizardService#processFinishEvents
	 *
	 * @description
	 * Does every required action after wizard is finished successfully
	 *
	 * @param {boolean} resetActionsCache Reset actions cache after the wizard is finished
	 * @param {boolean} resetNavigationCache Reset navigation cache after the wizard is finished
	 * @param {boolean} resetEnumCache Reset enumerations cache after the wizard is finished
	 * @param {boolean} resetWidgetsCache Reset all the content widgets cache after the wizard is finished
	 * @param {string[]} resetCacheForWidgets An array of widget ids to reset cache for
	 * @param {boolean} resetSchemaLocalCache Reset schema cache after the wizard is finished
	 * @param {boolean} reloadPage Reload the page after the wizard is finished.
	 * @param postAction
	 */
	function processFinishEvents(resetActionsCache, resetNavigationCache, resetEnumCache, resetWidgetsCache, resetCacheForWidgets, resetSchemaLocalCache, reloadPage, postAction) {
		const promise =
			resetActionsCache || resetNavigationCache || resetEnumCache || resetWidgetsCache || resetCacheForWidgets
				? cacheService.resetServerCache({
						enumeration: resetEnumCache,
						navigation: resetNavigationCache,
						action: resetActionsCache,
						widgets: resetWidgetsCache || resetCacheForWidgets,
				  })
				: $q.when();

		promise.then(function () {
			if (resetSchemaLocalCache) {
				schemaService.clearTypes();
				schemaService.clearClasses();
			}

			if (postAction?.isActive && typeof postAction.run === 'function') {
				postAction.run();
			}

			if (reloadPage) {
				window.forceReload();
			}
		});
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.WizardService#getWizard
	 *
	 * @description
	 * Creates and returns Wizard
	 *
	 * @param {string} widgetId - Related Content Widget Wizard identifier.
	 * @param {mx.shell.WizardView} wizardView - Related wizard view
	 * @param {number} wizardMode - Shows if wizard view is created for create or edit dialog.
	 * @param {string} finishTitle - A title for wizard's finish button.
	 * @param {object} mainWizardRef - A reference to a main wizard.
	 *
	 * @returns {object} a promise which will be resolved with new instance of Widget
	 */
	function getWizard(widgetId, wizardView, finishTitle, wizardMode, mainWizardRef) {
		return contentWidgetService.getHtml(widgetId).then(function (data) {
			const result = prepareNonVisualControls(data.Template.NonVisualTemplate, mainWizardRef);
			data.Template.NonVisualTemplate = result.template;

			if (wizardView) {
				wizardView.helpSourceType = data.HelpSourceType || 0;
				wizardView.helpValue = data.HelpValue || '';
			}

			const wizard = initWizard(data, wizardView, widgetId, finishTitle, wizardMode);
			wizard.numOfExtraWizards = result.numOfExtraWizards;

			wizard.__widgetHtmlData = data;
			return wizard;
		});
	}

	function getWizardAndDataModel(
		typeName,
		wizardMode,
		widgetId,
		wizardView,
		actionId,
		actionName,
		finishTitle,
		customHeader,
		selectedObjects,
		outerContext,
		mainWizardRef,
		freeDataSourcePromise
	) {
		if (!Array.isArray(selectedObjects)) {
			selectedObjects = [];
		}
		const firstObject = selectedObjects.length > 0 ? selectedObjects[0] : {};
		const requests = [getWizard(widgetId, wizardView, finishTitle, wizardMode, mainWizardRef)];
		let isService = false;

		if (wizardMode !== mx.SolutionBuilderAgent.WizardMode.Default) {
			isService = externalServiceManager.isService(typeName);

			if (isService) {
				requests.push(externalServiceManager.getEntity(typeName, 'new'));
			} else if (wizardMode === mx.SolutionBuilderAgent.WizardMode.New) {
				requests.push(contentWidgetService.getNewDialogData(widgetId, '', null));
			} else {
				requests.push(contentWidgetService.getDialogData(widgetId, firstObject._id));
			}
		} else {
			requests.push(schemaService.getType(firstObject['Sys-EntityType'] || firstObject._type));
		}

		const getWizardAndDataModelPromise = $q.all(requests.concat(_gettingWizardAndDataModelPendingPromises)).then(function (result) {
			let defaultData = null;
			let wizard = result[0];
			const dataModelExtensions = JSON.parse(wizard.__widgetHtmlData.Template.DataModelExtensions || '{}');

			let typeDisplayName = wizard.__widgetHtmlData.EntityDisplayName || wizard.__widgetHtmlData.Name;

			if (wizardMode === mx.SolutionBuilderAgent.WizardMode.Edit && firstObject && result[1].entity) {
				firstObject._displayName = firstObject._displayName || result[1].entity._displayName;
				firstObject._name = firstObject._name || result[1].entity._name;
				firstObject._state = firstObject._state || result[1].entity._state;
			}

			if (wizardMode === mx.SolutionBuilderAgent.WizardMode.Default && selectedObjects.length > 0) {
				let mainClassName = '';

				if (result[1]) {
					typeDisplayName = result[1].DisplayName;
					mainClassName = result[1].MainClassName;
				}
				initWizardContextDescriptionHandlers(wizard, selectedObjects, firstObject, typeDisplayName, mainClassName);
			}

			initWizardContextDescription(wizard, selectedObjects, firstObject, typeDisplayName);

			let staticData = {};

			initStaticData(staticData, selectedObjects, widgetId, actionId, actionName, wizardMode);
			let entityName = '';

			_.set(staticData, 'Context.OuterContext', outerContext || {});

			if (wizard.__widgetHtmlData.DataDefinition && selectedObjects[0]) {
				defaultData = {};
				_.set(defaultData, 'DataDefinition.' + wizard.__widgetHtmlData.DataDefinition, selectedObjects[0]);
			}
			let promise;
			if (wizardMode !== mx.SolutionBuilderAgent.WizardMode.Default) {
				const entity = isService ? result[1].object : result[1].entity;
				entity.IsNew = entity['Sys-IsNew'];
				entityName = isService ? result[1].entityName : wizard.__widgetHtmlData.Entity;
				promise = dataModelService.loadDialogDataModel(entityName, entity, undefined, dataModelExtensions, freeDataSourcePromise, isService, staticData, defaultData);
			} else {
				promise = dataModelService.loadDataModel(dataModelExtensions, staticData, defaultData, freeDataSourcePromise);
			}

			return promise
				.then(function (dataModel) {
					dataModel.Context.__wizard = mainWizardRef || wizard;
					dataModel.Context.__selectedObjects = selectedObjects;
					dataModel.Context.__typeName = typeName;
					if (wizardView) {
						wizardView.context = wizardView.context || {};
						wizardView.context._archived = !!dataModel['Sys-Archived'];
					}

					return dataModel;
				})
				.then(updateWizardAfterDataModelLoad(wizardMode, isService, wizard.__widgetHtmlData, entityName, typeName, wizard, widgetId, wizardView, customHeader))
				.finally(function () {
					// Filter not just pending promises, which we should wait before starting processing new one
					_gettingWizardAndDataModelPendingPromises = _gettingWizardAndDataModelPendingPromises.filter(function (p) {
						return p.$$state.status === 0;
					});
				});
		});

		_gettingWizardAndDataModelPendingPromises.push(getWizardAndDataModelPromise);

		return getWizardAndDataModelPromise;
	}

	function initStaticData(staticData, selectedObjects, widgetId, actionId, actionName, wizardMode) {
		staticData.Context = getDefaultContext(widgetId, actionId, actionName);

		if (wizardMode === mx.SolutionBuilderAgent.WizardMode.Default) {
			selectedObjects.forEach(function (item) {
				staticData.Context.ObjectIds.push(item._id);
				staticData.Context.ObjectReferences.push(item);
			});
		}
		if (selectedObjects.length > 0) {
			const item = selectedObjects[0];

			staticData.Context.Object = {};
			angular.extend(staticData.Context.Object, item);
			staticData.Context.Object.Id = item._id;
			staticData.Context.Object.Type = item._type;
			staticData.Context.Object.DisplayName = item._displayName;

			staticData.Context.ObjectReference = staticData.Context.ObjectReferences[0];
		} else {
			staticData.Context.Object = {
				Id: '',
				Type: '',
				DisplayName: '',
			};
		}
	}

	function initWizardContextDescriptionHandlers(wizard, selectedObjects, firstObject, typeDisplayName, mainClassName) {
		const singleObject = selectedObjects.length === 1;

		if (singleObject) {
			wizard.displayContextFn = function () {
				eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.ForceOpenObjectDetails, firstObject);
			};
		} else {
			if (selectedObjects.length > 0) {
				const ids = selectedObjects.map(function (item) {
					return item['Sys-ObjectId2'] || item._id;
				});

				const details = {
					entityClassName: mainClassName,
					ids: ids,
					displayName: typeDisplayName,
				};

				wizard.displayContextFn = function () {
					eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.ForceOpenObjectsGrid, details);
				};
			}
		}
	}

	function initWizardContextDescription(wizard, selectedObjects, firstObject, typeDisplayName) {
		const single = selectedObjects.length === 1;
		const multi = selectedObjects.length > 1;

		if (single) {
			wizard.contextDescription = i18n.getFirstNotEmpty([firstObject._displayName, firstObject._name, firstObject.Title, firstObject.title], '_');
			wizard.contextDescription = wizard.contextDescription === '_' ? '' : wizard.contextDescription;
		} else if (multi) {
			wizard.contextDescription = typeDisplayName + ' [' + selectedObjects.length + ']';
		}
	}

	function updateWizardAfterDataModelLoad(wizardMode, isService, data, entityName, typeName, wizard, widgetId, wizardView, customHeader) {
		return function (dataModel) {
			if (wizard.resultPage) {
				wizard.resultPage.dataModel = dataModel;
			}
			wizard.pages.forEach(function (page) {
				page.loadSource = function (sourceName) {
					dataModelService.reloadSourceByPath(sourceName, dataModel, widgetId);
				};

				page.dataModel = dataModel;
			});

			if (!data.ServiceOperation && !data.DataDefinition) {
				const finishButton = wizard.pages[wizard.pages.length - 1].buttons.finish;
				const lastFinishCallback = finishButton.callback;
				let afterLastFinishCallback = function () {
					if (wizardMode !== mx.SolutionBuilderAgent.WizardMode.Default) {
						closeWizard(customHeader || data.Name, dataModel, wizardView);
					} else {
						wizard.finishDefer.resolve();
					}
				};

				if (wizardMode !== mx.SolutionBuilderAgent.WizardMode.Default) {
					afterLastFinishCallback = createSaveCallbackForCreateEditWizard(
						data,
						entityName,
						dataModel,
						widgetId,
						wizardView,
						isService,
						typeName,
						customHeader,
						finishButton,
						wizard
					);
				}
				finishButton.callback = function () {
					return lastFinishCallback().then(afterLastFinishCallback);
				};
			}

			let wizardHeaderText;
			if (customHeader) {
				wizardHeaderText = customHeader;
			} else {
				let contextDescription, wizardName;

				if (data.DataDefinition) {
					contextDescription = data.ClassDisplayName;
				} else {
					contextDescription = wizard.contextDescription;
				}

				wizardName = i18n.getFirstNotEmpty([wizardView && wizardView.displayName, wizardView && wizardView.name], contextDescription);

				if (data.DataDefinition && !wizardView.displayName) {
					wizardView.displayName = data.Title || data.Name;
				}

				if (contextDescription) {
					const contextDescriptionTemplate =
						wizardMode === mx.SolutionBuilderAgent.WizardMode.Default && !data.DataDefinition
							? '<a href="#" class="wizard-page--context-link">' + contextDescription + '</a>'
							: contextDescription;
					if (wizardName && contextDescription !== wizardName) {
						wizardHeaderText = i18n.getFormatted('shell.wizard.headerTemplate', wizardName, contextDescriptionTemplate);
					} else {
						wizardHeaderText = i18n.getFormatted('shell.wizard.emptyHeaderTemplate', contextDescription);
					}
				} else {
					wizardHeaderText = i18n.getFormatted('shell.wizard.emptyHeaderTemplate', wizardName);
				}
			}

			return {
				wizard: wizard,
				dataModel: dataModel,
				nonVisualTemplate: data.Template.NonVisualTemplate,
				wizardHeaderText: wizardHeaderText,
			};
		};
	}

	function createSaveCallbackForCreateEditWizard(data, entityName, dataModel, widgetId, wizardView, isService, typeName, customHeader, finishButton, wizard) {
		const entity = dataModel[entityName];

		if (isService) {
			return function () {
				return externalServiceManager.updateEntity(typeName, 'new', entity).then(function () {
					closeWizard(customHeader || data.Name, dataModel, wizardView, false, finishButton.successSubmitMessage);
				});
			};
		}
		return function () {
			wizard.confirm = false;
			return contentWidgetService.updateDialogData(widgetId, entity).then(
				function (data) {
					closeWizard(customHeader || data.Name, dataModel, wizardView, false, finishButton.successSubmitMessage);
					return objectMapper.mapItem(data && data.Entity);
				},
				function (error) {
					if (concurrencyTrackingService.isUpdateConflict(error)) {
						wizard.confirm = true;
						showObjectChangedNotification('', entity, entityName, widgetId, entity.$$initialEntity, wizard);
						return false;
					} else if (concurrencyTrackingService.isDeleted(error)) {
						showObjectDeletedNotification('', wizard);
						return false;
					}
					return $q.reject(error);
				}
			);
		};
	}

	function showObjectDeletedNotification(userName, wizard) {
		userName = userName || '';

		concurrencyTrackingService.showObjectDeletedNotification(userName, wizard.wrapView.name, wizard.wrapView).then(function (answer) {
			eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.SearchPageDataChanged);

			if (answer === 'close') {
				forceCloseWizard(wizard.wrapView.id);
			} else {
				setObjectDeleted(wizard);
			}
		});
	}

	function showObjectChangedNotification(userName, entity, entityName, widgetId, initialEntity, wizard) {
		if (!wizard.confirm) {
			return;
		}
		wizard.confirm = false;

		concurrencyTrackingService
			.showObjectChangedNotification(userName || '', wizard.wrapView.name, {
				view: wizard.wrapView,
				entityName: entityName,
				startEntityValue: initialEntity,
				finishEntityValue: dataModelService.serializeObject(entity),
				widgetId: widgetId,
				objectId: entity.ID,
				mergeDisabled: wizard.mergeDisabled,
			})
			.then(function (data) {
				switch (data.answer) {
					case 'merge':
						_.merge(entity, data.entity);
						if (data.save) {
							wizard.finishWizard();
						} else {
							wizard.confirm = true;
						}
						break;
					case 'reload':
						reloadWizard(wizard.wrapView.id, wizard);
						break;
					case 'cancel':
						if (wizard.mergeDisabled) {
							setWizardOutdated(wizard);
						} else {
							wizard.confirm = true;
						}
						break;
					case 'no-changes':
						wizard.confirm = true;
						break;
				}
			});
	}

	/* for dialogs as wizard */
	function setWizardOutdated(wizard) {
		wizard.allWizardPages[wizard.allWizardPages.length - 1].buttons.finish.isDisabled = true;
		wizard.showWarning = true;
		wizard.warningMessage = i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyObjectOutdated');
		wizard.warningMessageButtonLabel = i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmYes');
		wizard.warningMessageButtonHandler = function () {
			reloadWizard(wizard.wrapView.id, wizard);
		};
	}

	function setObjectDeleted(wizard) {
		wizard.allWizardPages[wizard.allWizardPages.length - 1].buttons.finish.isDisabled = true;
		wizard.errors = [
			{
				message: i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyObjectDeleted'),
				type: 'warning',
				active: true,
			},
		];
	}

	function reloadWizard(viewId, wizard) {
		const originalOptions = wizard.context.originalOptions;
		sidePanelService.closeView(viewId);

		const newView = getWizardView(originalOptions);
		sidePanelService.addView(newView).catch(mx.logCatch);
	}

	function forceCloseWizard(viewId) {
		sidePanelService.closeView(viewId);
	}

	function closeWizard(name, dataModel, wizardView, fullScreenMode, successSubmitMessage) {
		eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.SearchPageDataChanged);
		eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.ObjectDataChanged, {
			affectedObjectIds: dataModel.Context && dataModel.Context.ObjectIds,
			hideDetails: dataModel.Context && dataModel.Context.__hideDetails,
		});

		if (wizardView?.startedFromAction?.purpose === mx.shell.ActionPurpose.Delete) {
			wizardView.locals.selectedObjects.forEach(function (object) {
				eventService.dispatchEvent(mx.shell.EventCodes.ObjectRemovedByWizard, {
					EntityType: object['Sys-Entity'] || object._type || object.type,
					Id: object.ID || object._id || object.id,
				});
			});
		}

		if (typeof successSubmitMessage !== 'boolean' || successSubmitMessage !== false) {
			const actionTitle = (wizardView && (wizardView.displayName || wizardView.name)) || name;
			const message = successSubmitMessage || i18n.getFormatted('solutionBuilderAgent.actions.wizard.success', actionTitle);

			// if it's main wizard (not extra-wizard)
			if (wizardView?.locals?.wizard?.name === name) {
				notificationService.success(message);
			}
		}
		eventService.dispatchEvent(mx.shell.EventCodes.WizardPostAction, dataModel.Context);
		// TODO: NGR: this function was called twice. to be investigated
		//sidePanelService.closeView(wizardView && wizardView.locals && wizardView.locals.view);
		if (fullScreenMode !== undefined) {
			sideLayoutService.switchFullScreenMode(fullScreenMode);
		}
	}

	function getDefaultContext(widgetId, actionId, actionName) {
		return {
			WidgetId: widgetId,
			Object: {
				Id: '',
				Type: '',
				DisplayName: '',
			},
			ObjectIds: [],
			ObjectReference: [],
			ObjectReferences: [],
			ActionId: actionId,
			ActionName: actionName,
		};
	}

	function createWizardPage(widgetId, index, view, buttons, sharedWizardData) {
		return new WizardPage({
			id: widgetId + '_page_' + index,
			widgetId,
			orderNum: index + 1,
			title: view.Title,
			key: view.Key,
			wizardStepId: view.Id,
			template: view.Template,
			header: view.Header,
			controller: pagesCommonController,
			buttons: buttons,
			sharedWizardData: sharedWizardData,
		});
	}

	function createFinishButton(wizardData, wizardView, widgetId, finishTitle, wizardMode, page, isVisible, callback) {
		const serviceOperation = wizardData.ServiceOperation;
		const dataDefinition = wizardData.DataDefinition;

		const finishButtonTitle =
			serviceOperation || dataDefinition
				? finishTitle || i18n.get('solutionBuilderAgent.wizards.finish')
				: wizardMode !== mx.SolutionBuilderAgent.WizardMode.Default
				? i18n.get('solutionBuilderAgent.common.save_btn')
				: i18n.get('solutionBuilderAgent.common.close_btn');

		const finishButton = new WizardButton(finishButtonTitle, isVisible);
		let mainCallback;
		const submitCallback = function () {
			callback(finishButton.successSubmitMessage);
		};

		if (serviceOperation) {
			mainCallback = function () {
				return dataModelService
					.reloadSourceByPath('SubmitData', page.dataModel, widgetId, getActionContextHeader(page.dataModel.Context))
					.then(submitCallback)
					.catch((customError) => {
						if (typeof customError === 'string') {
							return $q.reject(false);
						}

						const msg = i18n.get('shell.mx-shell-error-message.default-description');
						notificationService.error(msg);
						return $q.reject({ data: msg });
					});
			};
		} else if (dataDefinition) {
			mainCallback = function () {
				submitCallback();
				return page.dataModel.DataDefinition[dataDefinition];
			};
		} else {
			mainCallback = function () {};
		}
		finishButton.callback = function () {
			return !!wizardView && wizardView.locals && wizardView.locals.originalOptions && wizardView.locals.originalOptions.checkActionAvailability
				? wizardView.locals.originalOptions.checkActionAvailability(wizardView.locals.selectedObjects).then(function (available) {
						if (available) {
							return mainCallback();
						}
						notificationService.error(i18n.get('solutionBuilderAgent.actions.actionUnavailable'));
						return $q.reject(true);
				  })
				: $q.when(mainCallback());
		};
		return finishButton;
	}

	function getActionContextHeader(context) {
		return context && actionCoRuService.getActionContextHeader(context.ActionId, context.ObjectIds);
	}

	function createCancelButton(wizardData, wizardView, wizardMode) {
		const isVisible = wizardData.ServiceOperation || wizardData.DataDefinition || wizardMode !== mx.SolutionBuilderAgent.WizardMode.Default;

		const cancelButton = new WizardButton(i18n.get('solutionBuilderAgent.wizards.cancel'), isVisible);

		cancelButton.callback = function () {
			return $q.when();
		};
		return cancelButton;
	}

	function initWizard(wizardData, wizardView, widgetId, finishTitle, wizardMode) {
		const sharedWizardData = {};
		const pages = [];
		const steps = [];
		let resultPage, currentPage;

		if (Array.isArray(wizardData.Template.Views)) {
			const views = wizardData.Template.Views.sort(function (a, b) {
				return (a.Position || 0) - (b.Position || 0);
			});

			const resultView = _.find(views, { Type: mx.SolutionBuilderAgent.ContentWidgetViewType.ResultWizardPage });

			if (resultView) {
				const wizardButtons = {
					finish: new WizardButton(i18n.get('solutionBuilderAgent.common.close_btn')),
					cancel: {},
				};

				resultPage = createWizardPage(widgetId, views.length, resultView, wizardButtons, sharedWizardData);
			}

			views
				.filter((view) => view.Type !== mx.SolutionBuilderAgent.ContentWidgetViewType.ResultWizardPage)
				.forEach(function (view, index, views) {
					const previousView = views[index - 1];
					const nextView = views[index + 1];
					const position = index + 1;

					const wizardButtons = {};

					const page = createWizardPage(widgetId, index, view, wizardButtons, sharedWizardData);
					const finishResult = function (successSubmitMessage) {
						closeWizard(wizardData.Name, page.dataModel, wizardView, undefined, successSubmitMessage);
					};

					wizardButtons.finish = createFinishButton(wizardData, wizardView, widgetId, finishTitle, wizardMode, page, position === views.length, finishResult);
					wizardButtons.back = new WizardButton('<< ' + (previousView ? previousView.Title : i18n.get('solutionBuilderAgent.wizards.back')), position > 1);
					wizardButtons.next = new WizardButton((nextView ? nextView.Title : i18n.get('solutionBuilderAgent.wizards.next')) + ' >>', position < views.length);
					wizardButtons.cancel = createCancelButton(wizardData, wizardView, wizardMode);

					pages.push(page);

					steps.push(
						new WizardRoadmapStep({
							id: view.Id,
							orderNum: position,
							title: view.Title,
							key: view.Key,
							description: view.Description,
						})
					);

					if (index === 0) {
						currentPage = page;
					}
				});
		}

		return new Wizard({
			id: wizardData.Id,
			name: wizardData.Name,
			pages: pages,
			roadmap: new WizardRoadmap({
				title: wizardView ? wizardView.displayName : wizardData.Name,
				steps: steps,
			}),
			resultPage: resultPage,
			currentPage: currentPage,
			wizardMode: wizardMode,
			confirm: true,
			serviceOperation: wizardData.ServiceOperation,
			wizardView: wizardView,
		});
	}
}
